"use client";

import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { Award, Users, BookOpen, Headphones, Shield, Clock, CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";

interface Client {
  id: number;
  name: string;
  logo: string | null;
}

interface Benefit {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
}

const benefits: Benefit[] = [
  {
    icon: <Award className="w-8 h-8" />,
    title: "Certificação Reconhecida",
    description: "Certificados válidos em todo território nacional, reconhecidos pelas principais empresas do setor offshore.",
    features: ["Válido nacionalmente", "Reconhecido por empresas", "Registro digital"],
  },
  {
    icon: <Users className="w-8 h-8" />,
    title: "Instrutores Experientes",
    description: "Profissionais com ampla experiência prática no mercado offshore e formação técnica especializada.",
    features: ["Profissionais atuantes", "Experiência comprovada", "Didática moderna"],
  },
  {
    icon: <BookOpen className="w-8 h-8" />,
    title: "Material Didático Incluso",
    description: "Apostilas, vídeos e materiais complementares inclusos no valor do curso, sem custos adicionais.",
    features: ["Apostilas completas", "Videoaulas HD", "Acesso vitalício"],
  },
  {
    icon: <Headphones className="w-8 h-8" />,
    title: "Suporte Pós-Curso",
    description: "Acompanhamento e suporte técnico mesmo após a conclusão, tirando dúvidas e auxiliando na carreira.",
    features: ["Suporte via WhatsApp", "Comunidade exclusiva", "Orientação de carreira"],
  },
];

function BenefitCard({ benefit, index, isVisible }: { benefit: Benefit; index: number; isVisible: boolean }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className={`relative group transition-all duration-700 cursor-pointer ${
        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
      style={{ transitionDelay: `${index * 150}ms` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Card */}
      <div className={`relative bg-white rounded-2xl p-8 h-full border-2 transition-all duration-500 ${
        isHovered ? "border-[#2AABDE] shadow-xl shadow-[#2AABDE]/10" : "border-gray-100 shadow-lg"
      }`}>
        {/* Icon */}
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 transition-all duration-500 ${
          isHovered
            ? "bg-[#2AABDE] text-white rotate-6 scale-110"
            : "bg-[#2AABDE]/10 text-[#2AABDE]"
        }`}>
          {benefit.icon}
        </div>

        {/* Title */}
        <h3 className={`text-xl font-bold mb-3 transition-colors duration-300 ${
          isHovered ? "text-[#2AABDE]" : "text-gray-900"
        }`}>
          {benefit.title}
        </h3>

        {/* Description */}
        <p className="text-gray-600 mb-6 leading-relaxed">
          {benefit.description}
        </p>

        {/* Features List */}
        <ul className="space-y-2">
          {benefit.features.map((feature, idx) => (
            <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className={`w-4 h-4 flex-shrink-0 transition-colors duration-300 ${
                isHovered ? "text-[#2AABDE]" : "text-emerald-500"
              }`} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        {/* Decorative corner */}
        <div className={`absolute top-0 right-0 w-20 h-20 transition-opacity duration-500 ${
          isHovered ? "opacity-100" : "opacity-0"
        }`}>
          <div className="absolute top-0 right-0 w-full h-full bg-[#2AABDE]/5 rounded-bl-[100px]" />
        </div>

        {/* Number badge */}
        <div className={`absolute -top-3 -right-3 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-500 ${
          isHovered
            ? "bg-[#2AABDE] text-white scale-110"
            : "bg-gray-100 text-gray-400"
        }`}>
          0{index + 1}
        </div>
      </div>
    </div>
  );
}

export default function WhyChooseUs() {
  const [isVisible, setIsVisible] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [useGrayscale, setUseGrayscale] = useState(true);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL || "http://localhost:5000";

        // Buscar clientes
        const clientsResponse = await fetch(`${backendUrl}/api/website/public/clients`);
        if (clientsResponse.ok) {
          const clientsData = await clientsResponse.json();
          if (clientsData.success) {
            setClients(clientsData.data || []);
          }
        }

        // Buscar configuração de grayscale
        const logosResponse = await fetch(`${backendUrl}/api/website/public/logos`);
        if (logosResponse.ok) {
          const logosData = await logosResponse.json();
          if (logosData.success && logosData.data?.clients_grayscale !== undefined) {
            setUseGrayscale(logosData.data.clients_grayscale === "1" || logosData.data.clients_grayscale === true);
          }
        }
      } catch (error) {
        console.error("Erro ao carregar clientes:", error);
      }
    };
    fetchClients();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-[1400px] mx-auto px-4">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}>
          <span className="inline-block text-[#2AABDE] font-semibold text-sm uppercase tracking-wider mb-2">
            Por que nos escolher
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Nossos <span className="text-[#2AABDE]">Diferenciais</span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto">
            Investimos constantemente em qualidade para oferecer a melhor experiência de aprendizado
          </p>
          <div className="w-24 h-1.5 bg-gradient-to-r from-[#2AABDE] to-[#1a8ab8] mx-auto rounded-full mt-6" />
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((benefit, index) => (
            <BenefitCard
              key={index}
              benefit={benefit}
              index={index}
              isVisible={isVisible}
            />
          ))}
        </div>

        {/* Bottom Banner */}
        <div className={`mt-16 transition-all duration-1000 delay-500 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
        }`}>
          <div className="relative bg-gradient-to-r from-gray-900 to-gray-800 rounded-3xl p-8 md:p-12 overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-10">
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
                  backgroundSize: "32px 32px",
                }}
              />
            </div>

            {/* Gradient orb */}
            <div className="absolute -right-20 -top-20 w-80 h-80 bg-[#2AABDE]/30 rounded-full blur-3xl" />

            <div className="relative flex flex-col md:flex-row items-center justify-between gap-8">
              <div className="text-center md:text-left">
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Pronto para iniciar sua jornada?
                </h3>
                <p className="text-gray-400 max-w-lg">
                  Junte-se a milhares de profissionais que já transformaram suas carreiras com nossos cursos
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button className="px-8 py-4 bg-[#2AABDE] text-white font-semibold rounded-xl hover:bg-[#2599c9] transition-all duration-300 hover:shadow-lg hover:shadow-[#2AABDE]/30 whitespace-nowrap">
                  Simulador de Carreira
                </button>
                <button className="px-8 py-4 bg-transparent text-white font-semibold rounded-xl border-2 border-white/30 hover:bg-white/10 transition-all duration-300 whitespace-nowrap">
                  Ver Cursos
                </button>
              </div>
            </div>

            {/* Trust badges */}
            <div className="relative mt-8 pt-8 border-t border-white/10">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-8 text-white/60 text-sm">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  <span>Pagamento Seguro</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>Acesso Imediato</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  <span>Certificado Incluso</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Partners Carousel */}
        {clients.length > 0 && (
          <PartnersCarousel isVisible={isVisible} clients={clients} useGrayscale={useGrayscale} />
        )}
      </div>
    </section>
  );
}

function PartnersCarousel({ isVisible, clients, useGrayscale }: { isVisible: boolean; clients: Client[]; useGrayscale: boolean }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsToShow, setItemsToShow] = useState(6);

  // Ajustar quantidade de itens baseado no tamanho da tela
  useEffect(() => {
    const updateItemsToShow = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setItemsToShow(Math.min(2, clients.length)); // Mobile: 2 itens
      } else if (width < 768) {
        setItemsToShow(Math.min(3, clients.length)); // Tablet pequeno: 3 itens
      } else if (width < 1024) {
        setItemsToShow(Math.min(4, clients.length)); // Tablet: 4 itens
      } else {
        setItemsToShow(Math.min(6, clients.length)); // Desktop: 6 itens
      }
    };

    updateItemsToShow();
    window.addEventListener('resize', updateItemsToShow);
    return () => window.removeEventListener('resize', updateItemsToShow);
  }, [clients.length]);

  const maxIndex = Math.max(0, clients.length - itemsToShow);

  // Resetar índice quando itemsToShow muda
  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(0);
    }
  }, [itemsToShow, maxIndex, currentIndex]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  // Auto-play
  useEffect(() => {
    if (maxIndex > 0) {
      const interval = setInterval(nextSlide, 3000);
      return () => clearInterval(interval);
    }
  }, [maxIndex]);

  return (
    <div className={`mt-16 transition-all duration-1000 delay-700 ${
      isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
    }`}>
      {/* Header */}
      <div className="text-center mb-10">
        <span className="inline-block text-gray-500 font-semibold text-sm uppercase tracking-wider mb-2">
          Parcerias
        </span>
        <h3 className="text-2xl md:text-3xl font-bold text-gray-900">
          Clientes que <span className="text-gray-500">Confiam</span>
        </h3>
      </div>

      {/* Carousel Container */}
      <div className="relative">
        {/* Left Arrow */}
        {maxIndex > 0 && (
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 border border-gray-200 hover:bg-gray-100 cursor-pointer"
            aria-label="Anterior"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
        )}

        {/* Carousel Track */}
        <div className="overflow-hidden mx-8">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * (100 / itemsToShow)}%)` }}
          >
            {clients.map((client) => (
              <div
                key={client.id}
                className="flex-shrink-0 px-4"
                style={{ width: `${100 / itemsToShow}%` }}
              >
                <div className="bg-white rounded-xl p-4 h-32 flex items-center justify-center border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer group">
                  <div className={`relative w-full h-full flex items-center justify-center transition-all duration-300 ${
                    useGrayscale ? "grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100" : ""
                  }`}>
                    {client.logo ? (
                      <Image
                        src={client.logo}
                        alt={client.name}
                        fill
                        className="object-contain p-2"
                        unoptimized
                      />
                    ) : (
                      <span className="text-gray-400 font-semibold text-sm text-center">
                        {client.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Arrow */}
        {maxIndex > 0 && (
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 border border-gray-200 hover:bg-gray-100 cursor-pointer"
            aria-label="Próximo"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        )}
      </div>

      {/* Dots Indicator */}
      {maxIndex > 0 && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                currentIndex === index ? "bg-gray-600 w-6" : "bg-gray-300"
              }`}
              aria-label={`Ir para slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
